import { Eye, EyeOff, ImagePlus, Move, RefreshCcw, RotateCcw, RotateCw, Trash2 } from 'lucide-react';
import AiToolUploadDropzone from './AiToolUploadDropzone.jsx';
import StyledRangeSlider from './StyledRangeSlider.jsx';
import {
  clampBackgroundScale,
  loadBackgroundReferenceImage,
  normalizeBackgroundReferenceImage,
  normalizeBackgroundRotation,
  removeBackgroundReferenceImage,
  resetBackgroundReferenceImageTransform,
  updateBackgroundReferenceImage,
} from '../lib/floorPlanBackgroundImageState.js';

/**
 * @param {{
 *   background: import('../lib/floorPlanBackgroundImageState.js').BackgroundReferenceImage | null;
 *   metersPerPixel: number;
 *   editMode: boolean;
 *   onEditModeChange: (enabled: boolean) => void;
 *   onStateChange: (next: import('../lib/floorPlanDesignerState.js').DesignerState) => void;
 *   state: import('../lib/floorPlanDesignerState.js').DesignerState;
 *   onFitContent?: () => void;
 * }} props
 */
function FloorPlanBackgroundImageControls({
  background,
  metersPerPixel,
  editMode,
  onEditModeChange,
  onStateChange,
  state,
  onFitContent,
}) {
  const handleFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const objectUrl = URL.createObjectURL(file);
    const img = new window.Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = objectUrl;
    });
    URL.revokeObjectURL(objectUrl);
    const next = await loadBackgroundReferenceImage(state, file, {
      naturalWidth: img.naturalWidth,
      naturalHeight: img.naturalHeight,
      metersPerPixel,
    });
    onStateChange(next);
    requestAnimationFrame(() => {
      onFitContent?.();
    });
  };

  const normalizedBackground = background
    ? normalizeBackgroundReferenceImage(background, metersPerPixel)
    : null;

  return (
    <div className="floor-plan-background-controls">
      <h3 className="new-design-subheading">
        <ImagePlus className="new-design-subheading-icon" size={18} strokeWidth={2} aria-hidden />
        Background reference
      </h3>

      {!normalizedBackground ? (
        <AiToolUploadDropzone
          inputId="floor-plan-background-upload"
          label="Upload reference image"
          sublabel="PNG / JPEG / WebP"
          accept="image/png,image/jpeg,image/webp"
          onFile={handleFile}
        />
      ) : (
        <>
          <div className="floor-plan-background-controls-actions">
            <label className="floor-plan-background-scale-label">
              <span className="sr-only">Scale</span>
              <input
                type="number"
                min={0.01}
                max={100}
                step={0.05}
                className="new-2d-project-input"
                value={normalizedBackground.scale}
                aria-label="Background scale"
                onChange={(e) =>
                  onStateChange(
                    updateBackgroundReferenceImage(state, {
                      scale: clampBackgroundScale(e.target.value),
                    }),
                  )
                }
              />
            </label>
            <button
              type="button"
              className="floor-plan-designer-tool-icon-btn"
              onClick={() =>
                onStateChange(
                  updateBackgroundReferenceImage(state, {
                    rotationDegrees: normalizeBackgroundRotation(normalizedBackground.rotationDegrees - 90),
                  }),
                )
              }
              title="Rotate left 90°"
              aria-label="Rotate left 90 degrees"
            >
              <RotateCcw size={18} aria-hidden />
            </button>
            <button
              type="button"
              className="floor-plan-designer-tool-icon-btn"
              onClick={() =>
                onStateChange(
                  updateBackgroundReferenceImage(state, {
                    rotationDegrees: normalizeBackgroundRotation(normalizedBackground.rotationDegrees + 90),
                  }),
                )
              }
              title="Rotate right 90°"
              aria-label="Rotate right 90 degrees"
            >
              <RotateCw size={18} aria-hidden />
            </button>
            <button
              type="button"
              className="floor-plan-designer-tool-icon-btn"
              onClick={() =>
                onStateChange(
                  updateBackgroundReferenceImage(state, {
                    visible: !normalizedBackground.visible,
                  }),
                )
              }
              title={normalizedBackground.visible ? 'Hide background' : 'Show background'}
              aria-label={normalizedBackground.visible ? 'Hide background' : 'Show background'}
            >
              {normalizedBackground.visible ? <EyeOff size={18} aria-hidden /> : <Eye size={18} aria-hidden />}
            </button>
            <button
              type="button"
              className={`floor-plan-designer-tool-icon-btn${editMode ? ' floor-plan-designer-tool-icon-btn--active' : ''}`}
              onClick={() => onEditModeChange(!editMode)}
              title={editMode ? 'Done moving' : 'Edit / move background'}
              aria-label={editMode ? 'Done moving background' : 'Edit and move background'}
              aria-pressed={editMode}
            >
              <Move size={18} aria-hidden />
            </button>
            <button
              type="button"
              className="floor-plan-designer-tool-icon-btn"
              onClick={() => onStateChange(resetBackgroundReferenceImageTransform(state))}
              title="Reset background transform"
              aria-label="Reset background transform"
            >
              <RefreshCcw size={18} aria-hidden />
            </button>
            <button
              type="button"
              className="floor-plan-designer-tool-icon-btn floor-plan-designer-tool-icon-btn--danger"
              onClick={() => onStateChange(removeBackgroundReferenceImage(state))}
              title="Remove background"
              aria-label="Remove background image"
            >
              <Trash2 size={18} aria-hidden />
            </button>
          </div>
          <label className="floor-plan-background-opacity">
            Opacity
            <StyledRangeSlider
              min={0.1}
              max={1}
              step={0.05}
              value={normalizedBackground.opacity}
              onChange={(e) =>
                onStateChange(
                  updateBackgroundReferenceImage(state, {
                    opacity: Number(e.target.value),
                  }),
                )
              }
              aria-label="Background image opacity"
            />
          </label>
        </>
      )}
    </div>
  );
}

export default FloorPlanBackgroundImageControls;
